/**
 * The crosswalk's generator (2.G deliverable 22; amendments 2.1 section 2, "Crosswalk"): the
 * three legacy packs of dnd5e (`dnd5e.monsters`, `dnd5e.spells`, `dnd5e.items`) matched by name
 * to the SRD library's entries, exactly, then by a normalized name, then by the reviewed manual
 * pairs below; each side's entry is used once, and a name two entries share is never matched.
 * A `*24` pack (SRD 5.2, 2024 rules) or any other pack is refused. The reference is a structural
 * list (`{kind, key, name}`), so this package never imports `@codex/srd`; the builder's script in
 * `packages/srd/test/differential/` builds it and writes the JSON. Pure and deterministic.
 */
export const CROSSWALK_PACKS = ['monsters', 'spells', 'items'];
export const PACK_KIND = {
    monsters: 'creature',
    spells: 'spell',
    items: 'item',
};
/** The document type of a pack's uuids (`Compendium.dnd5e.<pack>.<Type>.<id>`). */
export const PACK_DOCUMENT = {
    monsters: 'Actor',
    spells: 'Item',
    items: 'Item',
};
/**
 * Pairs a normalized name cannot find, each reviewed: the pack's name on the left, the SRD's
 * upstream key on the right, per pack.
 */
export const MANUAL_MATCHES = {
    monsters: {
        'Deep Gnome': 'srd_deep-gnome-svirfneblin',
    },
    spells: {},
    items: {
        'Hand Crossbow +1': 'srd_crossbow-hand-1',
        'Hand Crossbow +2': 'srd_crossbow-hand-2',
        'Hand Crossbow +3': 'srd_crossbow-hand-3',
        'Heavy Crossbow +1': 'srd_crossbow-heavy-1',
        'Heavy Crossbow +2': 'srd_crossbow-heavy-2',
        'Heavy Crossbow +3': 'srd_crossbow-heavy-3',
        'Light Crossbow +1': 'srd_crossbow-light-1',
        'Light Crossbow +2': 'srd_crossbow-light-2',
        'Light Crossbow +3': 'srd_crossbow-light-3',
        'Vicious Hand Crossbow': 'srd_vicious-weapon-crossbow-hand',
        'Vicious Heavy Crossbow': 'srd_vicious-weapon-crossbow-heavy',
        'Vicious Light Crossbow': 'srd_vicious-weapon-crossbow-light',
        'Half Plate Armor': 'srd_half-plate',
        'Adamantine Half Plate Armor': 'srd_adamantine-armor-half-plate',
        'Adamantine Plate Armor': 'srd_adamantine-armor-plate',
        'Adamantine Splint Armor': 'srd_adamantine-armor-splint',
        'Mithral Half Plate Armor': 'srd_mithral-armor-half-plate',
        'Mithral Plate Armor': 'srd_mithral-armor-plate',
        'Mithral Splint Armor': 'srd_mithral-armor-splint',
        'Two-Person Tent': 'srd_tent',
        'Ink Bottle': 'srd_ink-1-ounce-bottle',
        'Oil Flask': 'srd_lamp-oil-flask',
        'Flask of Holy Water': 'srd_holy-water-flask',
        Sledgehammer: 'srd_hammer-sledge',
        'Sling Bullet': 'srd_sling-bullets',
        'Blowgun Needle': 'srd_blowgun-needles',
        'Playing Cards Set': 'srd_playing-card-set',
    },
};
/** Case, accents, apostrophes and punctuation folded; words single-spaced. */
export function normalizeName(name) {
    return name
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[’'`]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
const FOUNDRY_ID = /^[A-Za-z0-9]{16}$/;
/** A pack's own documents: an item inside a container (a pack's contents) is not an entry. */
function docs(list) {
    const out = [];
    for (const d of list) {
        if (typeof d !== 'object' || d === null)
            continue;
        const { _id: id, name, system } = d;
        const container = system?.container;
        if (typeof container === 'string' && container !== '')
            continue;
        if (typeof id === 'string' && FOUNDRY_ID.test(id) && typeof name === 'string' && name !== '')
            out.push({ id, name });
    }
    return out;
}
const GENERIC = /\b(weapon|armor|sword|stone)\b/;
/**
 * The other spellings a name has between the two sources, normalized: "Clothes, fine" is "fine
 * clothes"; "Vicious Weapon (Dagger)" is "vicious dagger"; "Sword of Wounding (Longsword)" is
 * "longsword of wounding"; "Ioun Stone (Agility)" is "ioun stone of agility"; "Rations (1 day)"
 * is "rations". The name itself is never among them (the earlier passes read it).
 */
export function nameVariants(name) {
    const paren = /^(.*?)\s*\(([^()]{1,60})\)$/.exec(name.trim());
    const head = normalizeName(paren?.[1] ?? name);
    const inner = paren === null ? '' : normalizeName(paren[2]);
    const comma = /^([^,]{1,60}),\s*([^,]{1,60})$/.exec(paren?.[1] ?? name);
    const swapped = comma === null ? null : normalizeName(`${comma[2]} ${comma[1]}`);
    const out = new Set();
    if (paren !== null)
        out.add(head);
    if (swapped !== null)
        out.add(swapped);
    if (inner !== '') {
        for (const base of swapped === null ? [head] : [head, swapped]) {
            out.add(`${base} ${inner}`);
            out.add(`${inner} ${base}`);
            out.add(`${base} of ${inner}`);
            if (GENERIC.test(base))
                out.add(base.replace(GENERIC, inner));
        }
    }
    out.delete(normalizeName(name));
    out.delete('');
    return [...out].sort();
}
const byText = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
/** Counts of a key over a list, for the "unique on both sides" rule. */
function counts(list, key) {
    const n = new Map();
    for (const t of list)
        n.set(key(t), (n.get(key(t)) ?? 0) + 1);
    return n;
}
/**
 * Pairs a pack name and an SRD name when one's spellings meet the other's normalized name (or a
 * spelling of it), and that meeting is the only one either side has.
 */
function variants(docsLeft, refsLeft) {
    const spell = (name) => new Set([normalizeName(name), ...nameVariants(name)]);
    const docKeys = docsLeft.map((d) => spell(d.name));
    const refKeys = refsLeft.map((r) => spell(r.name));
    const partners = (keys, others) => others.flatMap((o, j) => ([...keys].some((k) => o.has(k)) ? [j] : []));
    const pairs = [];
    for (const [i, keys] of docKeys.entries()) {
        const refs = partners(keys, refKeys);
        if (refs.length !== 1)
            continue;
        const j = refs[0];
        if (partners(refKeys[j], docKeys).length !== 1)
            continue;
        pairs.push([docsLeft[i], refsLeft[j]]);
    }
    return pairs;
}
function matchPack(pack, documents, reference) {
    const kind = PACK_KIND[pack];
    let freeDocs = [...documents];
    let freeRefs = reference.filter((r) => r.kind === kind);
    const entries = [];
    const take = (doc, ref, match) => {
        entries.push({ kind, upstreamKey: ref.key, pack, compendiumId: doc.id, name: doc.name, match });
    };
    const pass = (keyOf, match) => {
        const inDocs = counts(freeDocs, (d) => keyOf(d.name));
        const inRefs = counts(freeRefs, (r) => keyOf(r.name));
        const usedDocs = new Set();
        const usedRefs = new Set();
        for (const doc of freeDocs) {
            const k = keyOf(doc.name);
            if (k === '' || inDocs.get(k) !== 1 || inRefs.get(k) !== 1)
                continue;
            const ref = freeRefs.find((r) => keyOf(r.name) === k);
            take(doc, ref, match);
            usedDocs.add(doc.id);
            usedRefs.add(ref.key);
        }
        freeDocs = freeDocs.filter((d) => !usedDocs.has(d.id));
        freeRefs = freeRefs.filter((r) => !usedRefs.has(r.key));
    };
    pass((n) => n, 'exact');
    pass(normalizeName, 'normalized');
    for (const [doc, ref] of variants(freeDocs, freeRefs))
        take(doc, ref, 'normalized');
    const pairedDocs = new Set(entries.map((e) => e.compendiumId));
    const pairedRefs = new Set(entries.map((e) => e.upstreamKey));
    freeDocs = freeDocs.filter((d) => !pairedDocs.has(d.id));
    freeRefs = freeRefs.filter((r) => !pairedRefs.has(r.key));
    const manual = MANUAL_MATCHES[pack];
    for (const [name, key] of Object.entries(manual)) {
        const doc = freeDocs.filter((d) => d.name === name);
        const ref = freeRefs.find((r) => r.key === key);
        if (doc.length !== 1 || ref === undefined)
            continue;
        take(doc[0], ref, 'manual');
        freeDocs = freeDocs.filter((d) => d !== doc[0]);
        freeRefs = freeRefs.filter((r) => r !== ref);
    }
    return { entries, unmatchedDocs: freeDocs, unmatchedRefs: freeRefs };
}
export function generateCrosswalk(snapshot, reference) {
    const names = Object.keys(snapshot.packs);
    for (const name of names) {
        if (/24$/.test(name))
            return { ok: false, reason: 'modern_pack', pack: name };
        if (!CROSSWALK_PACKS.includes(name))
            return { ok: false, reason: 'unknown_pack', pack: name };
    }
    const entries = [];
    const unmatchedPack = [];
    const unmatchedSrd = [];
    for (const pack of CROSSWALK_PACKS) {
        const list = snapshot.packs[pack];
        if (list === undefined)
            return { ok: false, reason: 'missing_pack', pack };
        const result = matchPack(pack, docs(list), reference);
        entries.push(...result.entries.sort((a, b) => byText(a.upstreamKey, b.upstreamKey)));
        unmatchedPack.push(...result.unmatchedDocs.map((d) => `${pack}/${d.id}`).sort(byText));
        unmatchedSrd.push(...result.unmatchedRefs.map((r) => r.key).sort(byText));
    }
    return {
        ok: true,
        crosswalk: {
            source: {
                system: 'dnd5e',
                version: snapshot.version,
                packs: CROSSWALK_PACKS.map((p) => `dnd5e.${p}`),
                manifestSha256: snapshot.manifestSha256,
            },
            entries,
            unmatched: { pack: unmatchedPack, srd: unmatchedSrd },
        },
    };
}
/**
 * The committed text of a crosswalk: two-space JSON and a final newline, with each list of
 * strings that fits in 100 columns on one line (the repository formatter's layout).
 */
export function crosswalkText(crosswalk) {
    const lines = JSON.stringify(crosswalk, null, 2).split('\n');
    const out = [];
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.endsWith('[')) {
            const items = [];
            let j = i + 1;
            while (j < lines.length && /^\s*"(?:[^"\\]|\\.)*",?$/.test(lines[j])) {
                items.push(lines[j].trim().replace(/,$/, ''));
                j += 1;
            }
            const close = lines[j]?.trim();
            if (items.length > 0 && (close === ']' || close === '],')) {
                const joined = `${line}${items.join(', ')}${close}`;
                if (joined.length <= 100) {
                    out.push(joined);
                    i = j;
                    continue;
                }
            }
        }
        out.push(line);
    }
    return `${out.join('\n')}\n`;
}
//# sourceMappingURL=generate.js.map